import { Component, Input, AfterContentInit, ContentChild, forwardRef, EventEmitter, Output,
  ContentChildren, QueryList, ElementRef, ViewChild, Optional, Renderer, OnInit, ViewEncapsulation
} from '@angular/core';
import { FormControlDirective, FormControlName, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { coerceBooleanProperty } from '../common/shared';

let nextId = 0;

export class PuiRadioChange {
  source: PuiRadioComponent;
  value?: any;
};

export const PUI_RADIO_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => PuiRadioGroupComponent),
  multi: true
};

@Component({
  exportAs: 'puiInputGroup',
  selector: 'pui-radio-group',
  providers: [PUI_RADIO_CONTROL_VALUE_ACCESSOR],
  styleUrls: ['radio.component.scss'],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div [ngClass]="getClasses()">
      <label class="control-label">{{label}}</label>
      <ng-content></ng-content>
    </div>
  `
})
export class PuiRadioGroupComponent implements AfterContentInit, ControlValueAccessor {
  @Input() inline: boolean;
  @Input() label: string;
  @Output() change: EventEmitter<PuiRadioChange> = new EventEmitter<PuiRadioChange>();

  @ContentChildren(forwardRef(() => PuiRadioComponent))
  _radios: QueryList<PuiRadioComponent>;

  private _value: any;
  private _name = `pui-radio-group-${nextId++}`;
  private _disabled: boolean;
  private _selected: PuiRadioComponent;
  private _isInitialized = false;

  onTouchedCallback: () => void = () => { };
  onChangeCallback: (_: any) => void = () => { };

  getClasses() {
    return {
      'form-group': true,
      'mt-radio-list': !this.inline,
      'mt-radio-inline': this.inline
    };
  }

  @Input()
  get name(): string {
    return this._name;
  }
  set name(val: string) {
    this._name = val;
    this._updateRadioButtonNames();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(val) {
    this._disabled = (val !== null && val !== false) ? true : null;
  }

  @Input()
  get value(): any {
    return this._value;
  }
  set value(val: any) {
    if (this._value !== val) {
      this._value = val;
      this._updateSelectedRadioFromValue();
      this._checkSelectedRadioButton();
    }
  }

  @Input()
  get selected() {
    return this._selected;
  }
  set selected(selected: PuiRadioComponent) {
    this._selected = selected;
    this.value = selected ? selected.value : null;
    this._checkSelectedRadioButton();
  }

  _checkSelectedRadioButton() {
    if (this.selected && !this._selected.checked) {
      this._selected.checked = true;
    }
  }

  ngAfterContentInit() {
    this._isInitialized = true;
  }

  private _updateRadioButtonNames(): void {
    if (this._radios) {
      this._radios.forEach(radio => {
        radio.name = this.name;
      });
    }
  }

  private _updateSelectedRadioFromValue(): void {
    const isAlreadySelected = this._selected && this._selected.value === this._value;
    if (this._radios && !isAlreadySelected) {
      this._selected = null;
      this._radios.forEach(radio => {
        radio.checked = this.value === radio.value;
        if (radio.checked) {
          this._selected = radio;
        }
      });
    }
  }

  _emitChangeEvent(): void {
    if (this._isInitialized) {
      const e = <PuiRadioChange>{
        source: this._selected,
        value: this._value
      };
      this.change.emit(e);
    }
  }

  writeValue(val: any) {
    this.value = val;
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }
}

@Component({
  selector: 'pui-radio',
  templateUrl: 'radio.component.html'
})
export class PuiRadioComponent implements OnInit {
  @Input() id = `pui-radio-${nextId++}`;
  @Input() name: string;
  @Input() label: string;

  private _checked: boolean;
  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(checked: boolean) {
    if (this._checked !== checked) {
      this._checked = checked;
      if (checked && this.radioGroup && this.radioGroup.value !== this.value) {
        this.radioGroup.selected = this;
      } else if (!checked && this.radioGroup && this.radioGroup.value === this.value) {
        this.radioGroup.selected = null;
      }
    }
    if (checked && this.radioGroup && this.radioGroup._radios) {
      // sets others to un-checked
      this.radioGroup._radios.forEach(radio => {
        if (radio !== this) {
          radio.checked = false;
        }
      });
    }
  }

  private _value: any;
  @Input()
  get value(): any {
    return this._value;
  }
  set value(val: any) {
    if (this._value !== val) {
      this._value = val;
      if (this.radioGroup) {
        if (!this.checked) {
          this.checked = this.radioGroup.value === val;
        } else {
          this.radioGroup.selected = null;
        }
      }
    }
  }

  private _disabled: boolean;
  @Input()
  get disabled(): boolean {
    return this._disabled || (this.radioGroup && this.radioGroup.disabled);
  }
  set disabled(val) {
    this._disabled = coerceBooleanProperty(val);
  }

  @Output() change: EventEmitter<PuiRadioChange> = new EventEmitter<PuiRadioChange>();

  @ViewChild('input') _inputElement: ElementRef;
  @ContentChild(FormControlName) controlName: FormControlDirective;
  @ContentChild(FormControlDirective) controlDirective: FormControlDirective;

  private radioGroup: PuiRadioGroupComponent;

  constructor(@Optional() radioGroup: PuiRadioGroupComponent, private _elementRef: ElementRef, private _renderer: Renderer) {
    this.radioGroup = radioGroup;
  }

  ngOnInit() {
    if (this.radioGroup) {
      this.checked = this.radioGroup.value === this._value;
      this.name = this.radioGroup.name;
    }
    // if (this.controlDirective) {
    //   this.control = this.controlDirective.control;
    // } else if (this.controlName) {
    //   this.control = this.controlName.control;
    // }
    // if (this.control.disabled) {
    //   this.disabled = true;
    // }
  }

  getClasses() {
    return {
      'mt-radio': true,
      'mt-radio-outline': true,
      'mt-radio-disabled': this.disabled
    };
  }

  private _emitChangeEvent() {
    this.change.emit(<PuiRadioChange>{
      source: this,
      value: this._value
    });
  }

  onBlur() {
    if (this.radioGroup) {
      this.radioGroup.onTouchedCallback();
    }
  }

  onClick(e: Event) {
    e.stopPropagation();
  }

  onChange(e: Event) {
    e.stopPropagation();
    const groupValueChange = this.radioGroup && this.radioGroup.value !== this.value;
    this.checked = true;
    this._emitChangeEvent();
    if (this.radioGroup) {
      this.radioGroup.onChangeCallback(this.value);
      this.radioGroup.onTouchedCallback();
      if (groupValueChange) {
        this.radioGroup._emitChangeEvent();
      }
    }
  }
}
